<template>
    <page-container v-if="visible" class="h-full">
        <page-header> Local Performance </page-header>

        <div v-if="loading" class="flex items-center justify-center h-96">
            <vx-icon-loading class="w-8 h-8 text-sky-500" />
        </div>

        <vx-alert v-else-if="error" color="danger" :closeable="false">
            <div class="flex items-center justify-between">
                <span>{{ error }}</span>
                <vx-button data-test="retry-button" size="sm" color="danger" @click="fetchEmbedUrl"> Retry </vx-button>
            </div>
        </vx-alert>

        <iframe
            v-else-if="embedUrl"
            ref="dashboardIframe"
            :src="embedUrl"
            class="w-full min-h-[500px] border-0 rounded-lg"
            allow="fullscreen"
            referrerpolicy="strict-origin-when-cross-origin"
        />
    </page-container>
</template>

<script lang="ts" setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { VxAlert, VxButton, VxIconLoading } from '@voxie/frontend-components';
import PageContainer from '~/components/general/PageContainer.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import { omniClient } from '~/services/omniClient';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { isQsrLocationTeam } from '~/utils/team';
import type { OmniDashboardFiltersPayload, OmniEmbedEventData, OmniSizeEventPayload } from '~/types/omni.types';

const teamId = Spark.state.currentTeam.id;
const { currentGroup } = useCurrentGroup();
const visible = !isQsrLocationTeam(teamId) || Boolean(currentGroup.value);

const loading = ref(true);
const error = ref<string | null>(null);
const embedUrl = ref<string | null>(null);
const dashboardIframe = ref<HTMLIFrameElement | null>(null);

const fetchEmbedUrl = async () => {
    loading.value = true;
    error.value = null;

    try {
        const response = await omniClient.getEmbedUrl(teamId);
        embedUrl.value = response.data.url;
    } catch (err) {
        error.value = 'Unable to load analytics. Please try again later.';
        console.error('Failed to fetch Omni embed URL:', err);
    } finally {
        loading.value = false;
    }
};

const setFrameHeight = (height: number) => {
    if (dashboardIframe.value) {
        dashboardIframe.value.style.height = `${height}px`;
    }
};

// QDvY2foS (Date Attribution) must stay in sync with UTnA7yX0 (Date) when the
// user changes the date range. They share the same filter kind/values but reference
// different topics/base_views/fieldNames, so we copy only the date values across.
const DATE_FILTER_SOURCE = 'UTnA7yX0';
const DATE_FILTER_TARGET = 'QDvY2foS';

// Keys that belong to the target filter's identity and must not be overwritten by the source.
const DATE_FILTER_IDENTITY_KEYS = ['topic', 'fieldName', 'base_view', 'label', 'hidden'] as const;

const syncDateFilter = (filters: OmniDashboardFiltersPayload) => {
    const source = filters[DATE_FILTER_SOURCE];
    if (!isQsrLocationTeam(teamId) || !source || !dashboardIframe.value?.contentWindow) {
        return;
    }

    const target = filters[DATE_FILTER_TARGET];
    const targetFilter = target?.filter ?? {};

    const identity = Object.fromEntries(
        DATE_FILTER_IDENTITY_KEYS.filter((key) => key in targetFilter).map((key) => [key, targetFilter[key]])
    );

    const synced = { ...source.filter, ...identity };

    const filterUrlParameter = `f--${DATE_FILTER_TARGET}=${encodeURIComponent(JSON.stringify(synced))}`;

    dashboardIframe.value.contentWindow.postMessage(
        { name: 'dashboard:filter-change-by-url-parameter', payload: { filterUrlParameter } },
        '*'
    );
};

const listener = (event: MessageEvent<OmniEmbedEventData<OmniSizeEventPayload | OmniDashboardFiltersPayload>>) => {
    if (event.data?.source !== 'omni') {
        return;
    }

    switch (event.data.name) {
        case 'size': {
            const { height } = event.data.payload as OmniSizeEventPayload;
            if (height) {
                setFrameHeight(height);
            }
            break;
        }
        case 'dashboard:filters': {
            syncDateFilter(event.data.payload as OmniDashboardFiltersPayload);
            break;
        }
    }
};

onMounted(() => {
    fetchEmbedUrl();
    window.addEventListener('message', listener);
});

onUnmounted(() => {
    window.removeEventListener('message', listener);
});
</script>
